import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import { API_BASE_URL } from '../../lib/constants';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton,
  Box,
  ListSubheader,
  Select,
  InputLabel,
  FormHelperText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SignatureCanvas from 'react-signature-canvas';
import ShareIcon from '@mui/icons-material/Share';
import LocalHospital from '@mui/icons-material/LocalHospital';

import theme from '../../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import { useNotification } from '../../contexts/NotificationContext';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, clearDraft } from '../../utils/draftUtils';
const convertYesNoToBoolean = (value) => {
  if (value === 'yes') return true;  // Converts 'yes' to true
  if (value === 'no') return false;  // Converts 'no' to false
  // Converts other values to boolean (false for undefined, null, or other falsy values)
};

const SurgicalSafetyChecklist = () => {
  const { showNotification } = useNotification();
  const signaturePadRef = useRef(null);
  const uhid_no = useParams().uhid_no;
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const [loading, setLoading] = useState(false); // <-- Add this line
  const [error, setError] = useState(""); // <-- Add this line
  const [formData, setFormData] = useState({
    // Patient Information
    identity_confirmed: false,
    site_confirmed: false,
    procedure_confirmed: false,
    consent_confirmed: false,
    site_marked: false,
    anesthesia_safety_check: false,
    pulse_oximeter_functioning: false,
    known_allergy: false,
    difficult_airway: false,
    difficult_airway_assistance_available: false,
    risk_of_500ml_blood_loss: false,
    adequate_iv_fluids_planned: false,
    blood_standby: false,
    serology_status_negative: false,
    serology_status_positive: false,
    ot_nurse_name: '',
    ot_nurse_signature: '', // Signature data URL
    team_introduction: false,
    patient_confirmation: false,
    site_confirmation: false,
    procedure_confirmation: false,
    anticipated_critical_event: false,
    anesthesia_team_review: false,
    nursing_team_review: false,
    antibiotic_prophylaxis: false,
    essential_imaging_displayed: false,
    anesthesiologist_name: '', // Assuming you have a field for this
    anesthesiologist_signature: '', // Assuming you have a signature for this
    nurse_verbal_confirmation: false,
    procedure_recorded: false,
    instrument_sponge_needle_count_checked: false,
    specimen_labelled: false,
    equipment_problem: false,
    recovery_concerns: false,
    post_op_instruction_by_surgeon: false,
    post_op_instruction_by_anesthesiologist: false,
    recovery_charting_done: false,

    surgeon_name: '',
    surgeon_signature: '',


    uhid_no: uhid_no || "",
    ip_no: '',
    ward_room_no: '',
    procedure_name: '',
    primary_consultant: '',
    date_time_of_surgery: '',
  });

  // Fetch next UHID & IP_No on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [ipRes] = await Promise.all([

          axios.get(`${API_BASE_URL}/surgical_next_ip`)
        ]);
        setFormData(prev => ({
          ...prev,

          ip_no: ipRes.data.next_ip_no
        }));
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchInitialData();
  }, []);


  const formatDate = (date) => {
    if (!date) return ''; // or handle it however you'd like
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      console.error("Invalid date:", date);
      return '';
    }

    return format(new Date(date), 'yyyy-MM-dd HH:mm:ss');
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };
  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };
  const handleSignatureClose = () => {
    setSignatureDialogOpen(false)
  }
  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();

    // Removed undefined 'imageToBase64' function call
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(',')[1],
    }));
    setSignatureDialogOpen(false);
  };
  const navigate = useNavigate();
  const [success, setSuccess] = useState("");
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'ward_room_no':
        if (!value || !value.trim()) error = "Ward/Room No. is required";
        break;
      case 'procedure_name':
        if (!value || !value.trim()) error = "Procedure name is required";
        break;
      case 'primary_consultant':
        if (!value) error = "Primary Consultant is required";
        break;
      case 'date_time_of_surgery':
        if (!value) error = "Date & Time is required";
        break;

      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const validate = () => {
    const requiredFields = [
      { key: 'ward_room_no', label: 'Ward / Room No.' },
      { key: 'procedure_name', label: 'Procedure Name' },
      { key: 'primary_consultant', label: 'Primary Consultant' },
      { key: 'date_time_of_surgery', label: 'Date & Time of Surgery' },

    ];
    const tempErrors = {};
    requiredFields.forEach(({ key, label }) => {
      if (!formData[key] || String(formData[key]).trim() === '')
        tempErrors[key] = `${label} is required`;
    });
    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) {
      const firstKey = Object.keys(tempErrors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      showNotification('error', requiredFields.filter(f => tempErrors[f.key]).map(f => f.label));
    }
    return Object.keys(tempErrors).length === 0;
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;


    const payload = {
      identity_confirmed: formData.identity_confirmed,
      site_confirmed: formData.site_confirmed,
      procedure_confirmed: formData.procedure_confirmed,
      consent_confirmed: formData.consent_confirmed,
      site_marked: convertYesNoToBoolean(formData.site_marked),
      anesthesia_safety_check: formData.anesthesia_safety_check,
      pulse_oximeter_functioning: formData.pulse_oximeter_functioning,
      known_allergy: convertYesNoToBoolean(formData.known_allergy),
      difficult_airway: formData.difficult_airway,
      difficult_airway_assistance_available: convertYesNoToBoolean(formData.difficult_airway_assistance_available),
      risk_of_500ml_blood_loss: convertYesNoToBoolean(formData.risk_of_500ml_blood_loss),
      adequate_iv_fluids_planned: convertYesNoToBoolean(formData.adequate_iv_fluids_planned),
      blood_standby: formData.blood_standby,
      serology_status_negative: formData.serology_status_negative,
      serology_status_positive: formData.serology_status_positive,
      ot_nurse_name: formData.ot_nurse_name,
      ot_nurse_signature: formData.ot_nurse_signature,
      team_introduction: formData.team_introduction,
      patient_confirmation: formData.patient_confirmation,
      site_confirmation: formData.site_confirmation,
      procedure_confirmation: formData.procedure_confirmation,
      anticipated_critical_event: formData.anticipated_critical_event,
      anesthesia_team_review: formData.anesthesia_team_review,
      nursing_team_review: formData.nursing_team_review,
      antibiotic_prophylaxis: convertYesNoToBoolean(formData.antibiotic_prophylaxis),
      essential_imaging_displayed: convertYesNoToBoolean(formData.essential_imaging_displayed),
      anesthesiologist_name: formData.anesthesiologist_name,
      anesthesiologist_signature: formData.anesthesiologist_signature,
      nurse_verbal_confirmation: formData.nurse_verbal_confirmation,
      procedure_recorded: formData.procedure_recorded,
      instrument_sponge_needle_count_checked: convertYesNoToBoolean(formData.instrument_sponge_needle_count_checked),
      specimen_labelled: formData.specimen_labelled,
      equipment_problem: formData.equipment_problem,
      recovery_concerns: formData.recovery_concerns,
      post_op_instruction_by_surgeon: formData.post_op_instruction_by_surgeon,
      post_op_instruction_by_anesthesiologist: formData.post_op_instruction_by_anesthesiologist,
      recovery_charting_done: formData.recovery_charting_done,
      surgeon_name: formData.surgeon_name,
      surgeon_signature: formData.surgeon_signature,
      uhid_no: formData.uhid_no,
      ip_no: formData.ip_no,
      ward_room_no: formData.ward_room_no,
      procedure_name: formData.procedure_name,
      primary_consultant: formData.primary_consultant,
      date_time_of_surgery: formatDate(formData.date_time_of_surgery),
    };

    console.log('Payload to send:', payload);

    try {
      if (success === "success") {
        const responseUpdate = await axios.put(`${API_BASE_URL}/update_surgical_safety/${uhid_no}`, payload);
        showNotification('success', 'Surgical Safety Checklist updated successfully!');
      } else {
        const responseCreate = await axios.post(`${API_BASE_URL}/surgical_safety`, payload);
        console.log('Response from POST:', responseCreate.data);
        showNotification('success', 'Surgical Safety Checklist added successfully!');
      }
      clearSavedData();
      // Clear draft after successful submission
      clearDraft(DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST, uhid_no);
      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });

    } catch (error) {
      console.error('There was an error submitting the form!', error.response?.data || error.message);
      showNotification('error', 'Error in submitting Surgical Safety Checklist form!');
    }
  };



  const handleCheckboxChange = (event, value, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value, // Dynamically update the field (either site_marked or other)
    }));
  };

  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);

      console.log(response.data);
      setFormData(prev => ({
        ...prev,

        name: response.data.name

      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };
  useEffect(() => {
    // Load draft if exists
    const draft = loadDraft(DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST, uhid_no);
    if (draft && draft.formData) {
      setFormData(draft.formData);
      console.log('Draft loaded for Surgical Safety Checklist');
    }

    axios.get(`${API_BASE_URL}/get_surgical_safety/${uhid_no}`)
      .then((response) => {
        console.log('Fetched data:', response.data);
        const dateFields = [
          "date_time_of_surgery",

        ];

        // Format each date field and update the formData
        const formattedData = dateFields.reduce((acc, field) => {
          if (response.data[field]) {
            acc[field] = new Date(response.data[field]).toISOString().slice(0, 16);
          }
          return acc;
        }, {});


        // Convert all signatures to base64
        const signatureFields = [
          "ot_nurse_signature",
          "anesthesiologist_signature",
          "surgeon_signature",

        ];



        // Set the formData with formatted dates
        setFormData({
          ...response.data,
          ...formattedData,
          adequate_iv_fluids_planned: response.data.adequate_iv_fluids_planned ? "yes" : "no",
          adequate_iv_fluids_administered: response.data.adequate_iv_fluids_administered ? "yes" : "no",
          risk_of_500ml_blood_loss: response.data.risk_of_500ml_blood_loss ? "yes" : "no",
          difficult_airway_assistance_available: response.data.difficult_airway_assistance_available ? "yes" : "no",
          instrument_sponge_needle_count_checked: response.data.instrument_sponge_needle_count_checked ? "yes" : "no",
          essential_imaging_displayed: response.data.essential_imaging_displayed ? "yes" : "no",
          antibiotic_prophylaxis: response.data.antibiotic_prophylaxis ? "yes" : "no",
          known_allergy: response.data.known_allergy ? "yes" : "no",

          site_marked: response.data.site_marked ? "yes" : "no",
          ...signatureFields.reduce((acc, field) => {
            if (response.data[field]) {
              acc[field] = response.data[field]; // Directly assign the base64 data from backend
            }
            return acc;
          }, {}),
        });
        setSuccess("success")

      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, [uhid_no]);

  const wardRoomOptions = [
    "ICU-101",
    "ICU-102",
    "Ward-A1",
    "Ward-A2",
    "Private-301",
    "General-201"
  ];
  const procedureOptions = [
    "Appendectomy",
    "Cholecystectomy",
    "Cataract Surgery",
    "Cesarean Section",
    "Heart Bypass Surgery",
    "Knee Replacement",
    "Angioplasty"
  ];

  const consultantOptions = [
    "Dr. Smith",
    "Dr. Johnson",
    "Dr. Patel",
    "Dr. Lee",
    "Dr. Singh",
    "Dr. Ahmed"
  ];
  const handleNameChange = (e) => {
    const regex = /^[a-zA-Z\s.-]*$/; // allow letters, spaces, dot, hyphen
    const { name, value } = e.target;
    if (value === '' || regex.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    }
  };
  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_for_surgical_api_share_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/surgicalSafetyChecklist/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };
  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`Surgical_SafetyForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`Surgical_SafetyForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setFormData(saved);
    }
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`Surgical_SafetyForm_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_Surgical_Safety_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_Surgical_Safety_${uhid_no}`, JSON.stringify({
        formName: 'Surgical Safety Checklist',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/surgicalSafetyChecklist/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const handleBack = () => {
    navigate(`/mainPage/${formData.uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });// Navigate to the previous page
  };
  const doctorGroups = {
    Cardiologists: [
      "Dr. John Smith",
      "Dr. Emily Johnson",
      "Dr. Michael Brown",
      "Dr. Sarah Davis",
      "Dr. William Wilson",
      "Dr. Linda Martinez"
    ],
    Neurologists: [
      "Dr. Robert Lee",
      "Dr. Patricia Taylor",
      "Dr. James Anderson",
      "Dr. Barbara Thomas",
      "Dr. David Jackson",
      "Dr. Susan White"
    ],
    Orthopedics: [
      "Dr. Richard Garcia",
      "Dr. Jennifer Harris",
      "Dr. Charles Clark",
      "Dr. Jessica Lewis",
      "Dr. Thomas Robinson",
      "Dr. Karen Walker"
    ],
    Dermatologists: [
      "Dr. Nancy Young",
      "Dr. Matthew Hall",
      "Dr. Donna Allen",
      "Dr. Steven King",
      "Dr. Michelle Wright",
      "Dr. George Scott"
    ],
    Gynecologists: [
      "Dr. Sandra Green",
      "Dr. Kevin Adams",
      "Dr. Laura Baker",
      "Dr. Mark Nelson",
      "Dr. Angela Carter",
      "Dr. Brian Mitchell"
    ],
    Pediatricians: [
      "Dr. Melissa Perez",
      "Dr. Eric Roberts",
      "Dr. Deborah Turner",
      "Dr. Jason Phillips",
      "Dr. Rebecca Campbell",
      "Dr. Andrew Parker"
    ],
    AnaesthesiaSpecialists: [
      "Dr. Anil Mehta",
      "Dr. Priya Iyer",
      "Dr. Rakesh Nair",
      "Dr. Neha Sharma",
      "Dr. Vivek Rao",
      "Dr. Aarti Desai"
    ]
  };
  const nurseGroups = {
    ICU: [
      "Nurse Anna Smith",
      "Nurse Brian Lee",
      "Nurse Carol White",
      "Nurse David Green",
      "Nurse Emma Black",
      "Nurse Frank Harris",
    ],
    Pediatrics: [
      "Nurse Emily Davis",
      "Nurse Frank Miller",
      "Nurse Grace Wilson",
      "Nurse Hannah Moore",
      "Nurse Ian Clark",
      "Nurse Julia Lewis",
    ],
    GeneralWard: [
      "Nurse Henry Brown",
      "Nurse Irene Clark",
      "Nurse Jack Harris",
      "Nurse Kelly Martinez",
      "Nurse Liam Thompson",
      "Nurse Mia Anderson",
    ],
    Emergency: [
      "Nurse Karen Scott",
      "Nurse Larry Adams",
      "Nurse Monica Turner",
      "Nurse Nathan Hill",
      "Nurse Olivia King",
      "Nurse Peter Wright",
    ],
    Surgery: [
      "Nurse Quinn Young",
      "Nurse Rachel Walker",
      "Nurse Samuel Hall",
      "Nurse Tina Allen",
      "Nurse Victor Baker",
      "Nurse Wendy Nelson",
    ],
    Maternity: [
      "Nurse Xavier Perez",
      "Nurse Yolanda Roberts",
      "Nurse Zoe Campbell",
      "Nurse Aaron Phillips",
      "Nurse Bethany Parker",
      "Nurse Chris Turner",
    ],
  };
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              SURGICAL SAFETY CHECKLIST
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            {/* Left-aligned Back Button */}
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("SURGICAL SAFETY CHECKLIST", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>

            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Patient Information
            </Typography>
            <Grid container spacing={2}>
              {/*  <Grid item xs={12} sm={6}>
              <TextField
                label="Patient Name"
                name="patientName"
                value={formData.patientName}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Age"
                name="age"
                type="number"
                value={formData.age}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                fullWidth
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid> */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="UHID No"
                  name="uhid_no"
                  value={formData.uhid_no}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true, }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="IP NO"
                  name="ip_no"
                  value={formData.ip_no}
                  onChange={handleChange}
                  required
                  InputProps={{ readOnly: true, }}
                  fullWidth
                // InputProps={{readOnly: true,}}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={wardRoomOptions}
                  value={formData.ward_room_no}
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      ward_room_no: newInputValue,
                    }));
                    if (errors.ward_room_no) {
                      setErrors((prev) => ({ ...prev, ward_room_no: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ward/Room No."
                      name="ward_room_no"
                      required
                      fullWidth
                      error={!!errors.ward_room_no}
                      helperText={errors.ward_room_no}
                      onBlur={handleBlur}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={procedureOptions}
                  value={formData.procedure_name}
                  onInputChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      procedure_name: newValue,
                    }));
                    if (errors.procedure_name) {
                      setErrors((prev) => ({ ...prev, procedure_name: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Name of Procedure/Surgery"
                      name="procedure_name"
                      required
                      fullWidth
                      error={!!errors.procedure_name}
                      helperText={errors.procedure_name}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>

                <FormControl fullWidth required sx={{ mt: 1 }}>
                  <InputLabel id="primary-consultant-label">Primary Consultant</InputLabel>
                  <Select
                    labelId="primary-consultant-label"
                    label="Primary Consultant"
                    name="primary_consultant"
                    value={formData.primary_consultant || ""}
                    onChange={handleNameChange}
                    onBlur={handleBlur}
                    error={!!errors.primary_consultant}
                  >
                    <MenuItem value="">
                      <em>Select Doctor</em>
                    </MenuItem>
                    {Object.entries(doctorGroups).map(([specialty, doctors]) => [
                      <ListSubheader key={specialty}>{specialty}</ListSubheader>,
                      doctors.map((doctor, idx) => (
                        <MenuItem key={`${specialty}-${idx}`} value={doctor}>
                          {doctor}
                        </MenuItem>
                      ))
                    ])}
                  </Select>
                  {errors.primary_consultant && <FormHelperText>{errors.primary_consultant}</FormHelperText>}
                </FormControl>

              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Date of Surgery"
                  name="date_time_of_surgery"
                  type="DateTime-local" inputProps={{
                    min: new Date().toISOString().slice(0, 16),
                  }}
                  value={formData.date_time_of_surgery}
                  onChange={handleChange}
                  error={!!errors.date_time_of_surgery}
                  helperText={errors.date_time_of_surgery}
                  onBlur={handleBlur}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>


            <Accordion sx={{ marginTop: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Before Induction (Sign In)</Typography>
              </AccordionSummary>
              <AccordionDetails>

                <h5>Patient has confirmed</h5>
                <FormControlLabel
                  control={<Checkbox checked={formData.identity_confirmed} onChange={handleChange} name="identity_confirmed" />}
                  label="Identity"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.site_confirmed} onChange={handleChange} name="site_confirmed" />}
                  label="Site"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.procedure_confirmed} onChange={handleChange} name="procedure_confirmed" />}
                  label="Procedure"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.consent_confirmed} onChange={handleChange} name="consent_confirmed" />}
                  label="Consent"
                />
                <hr></hr>
                <h5>Site marked</h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.site_marked === "yes"} // Only check the "Site marked" checkbox if the value is "yes"
                      onChange={(event) => handleCheckboxChange(event, "yes", "site_marked")}
                      name="site_marked_yes"
                    />
                  }
                  label="Site marked"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.site_marked === "no"} // Only check the "NA" checkbox if the value is "no"
                      onChange={(event) => handleCheckboxChange(event, "no", "site_marked")}
                      name="site_marked_no"
                    />
                  }
                  label="NA"
                />

                <hr></hr>
                <FormControlLabel control={<Checkbox checked={formData.anesthesia_safety_check} onChange={handleChange} name="anesthesia_safety_check" />} label="Anesthesia safety check completed" />
                <FormControlLabel control={<Checkbox checked={formData.pulse_oximeter_functioning} onChange={handleChange} name="pulse_oximeter_functioning" />} label="Pulse oximeter on patient & functioning" />
                <h5>Does patient have:</h5>

                <h5>Known allergy</h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.known_allergy === "yes"} // Only check the "yes" checkbox if the value is "yes"
                      onChange={(event) => handleCheckboxChange(event, "yes", "known_allergy")}
                      name="known_allergy_yes"
                    />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.known_allergy === "no"} // Only check the "no" checkbox if the value is "no"
                      onChange={(event) => handleCheckboxChange(event, "no", "known_allergy")}
                      name="known_allergy_no"
                    />
                  }
                  label="No"
                />

                <hr></hr>
                <h5>Difficult airway/aspiration risk? </h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.difficult_airway_assistance_available === "yes"}
                      onChange={(event) => handleCheckboxChange(event, "yes", "difficult_airway_assistance_available")
                      }
                      name="difficult_airway_assistance_available_yes"
                    />
                  }
                  label="Yes, equipment/assistance available"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.difficult_airway_assistance_available === "no"}
                      onChange={(event) => handleCheckboxChange(event, "no", "difficult_airway_assistance_available")}
                      name="difficult_airway_assistance_available_no"
                    />
                  }
                  label="No"
                />

                <h5>Risk of{'>'}500 blood loss </h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.risk_of_500ml_blood_loss === "yes"} // Only check the "yes" checkbox if the value is "yes"
                      onChange={(event) => handleCheckboxChange(event, "yes", "risk_of_500ml_blood_loss")}
                      name="risk_of_500ml_blood_loss_yes"
                    />
                  }
                  label="Yes, adequate intravenous access/fluids planned"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.risk_of_500ml_blood_loss === "no"} // Only check the "no" checkbox if the value is "no"
                      onChange={(event) => handleCheckboxChange(event, "no", "risk_of_500ml_blood_loss")}
                      name="risk_of_500ml_blood_loss_no"
                    />
                  }
                  label="No"
                />

                <h5>Blood stand by kept</h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.adequate_iv_fluids_planned === "yes"} // Only check the "yes" checkbox if the value is "yes"
                      onChange={(event) => handleCheckboxChange(event, "yes", "adequate_iv_fluids_planned")}
                      name="adequate_iv_fluids_planned_yes"
                    />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.adequate_iv_fluids_planned === "no"} // Only check the "no" checkbox if the value is "no"
                      onChange={(event) => handleCheckboxChange(event, "no", "adequate_iv_fluids_planned")}
                      name="adequate_iv_fluids_planned_no"
                    />
                  }
                  label="No"
                />
                <h5>Serology status done </h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.serology_status_negative}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            serology_status_negative: true,
                            serology_status_positive: false
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            serology_status_negative: false
                          }));
                        }
                      }}
                      name="serology_status_negative"
                    />
                  }
                  label="Negative"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.serology_status_positive}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            serology_status_positive: true,
                            serology_status_negative: false
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            serology_status_positive: false
                          }));
                        }
                      }}
                      name="serology_status_positive"
                    />
                  }
                  label="Positive"
                />
                <hr></hr>
                <center><h5>OT NURSE</h5></center>
                <Grid container spacing={2}>
                  {[
                    {

                      key: "ot_nurse_signature",
                      key1: "ot_nurse_name",
                      label: "NURSE",

                    },


                  ].map((person, index) => (
                    <Grid item xs={12} key={index}>
                      <FormControl fullWidth sx={{ marginTop: 1 }}>
                        <InputLabel id={`${person.key1}-label`}>Name ({person.label})</InputLabel>
                        <Select
                          labelId={`${person.key1}-label`}
                          id={person.key1}
                          name={person.key1}
                          value={formData[person.key1] || ""}
                          label={`Name (${person.label})`}
                          onChange={handleNameChange}
                          onBlur={handleBlur}
                          error={!!errors[person.key1]}
                        >
                          <MenuItem value="">
                            <em>Select Nurse</em>
                          </MenuItem>
                          {Object.entries(nurseGroups).map(([groupName, nurses]) => (
                            <ListSubheader key={groupName}>{groupName}</ListSubheader>,
                            nurses.map((nurse, idx) => (
                              <MenuItem key={`${groupName}-${idx}`} value={nurse}>
                                {nurse}
                              </MenuItem>
                            ))
                          ))}
                        </Select>
                        {errors[person.key1] && <FormHelperText>{errors[person.key1]}</FormHelperText>}
                      </FormControl>

                      <Button variant="outlined" color="primary" sx={{ marginTop: 2 }} onClick={() => handleSignatureOpen(person.key)}> Sign Here </Button>
                      {formData[person.key] && <img src={`data:image/png;base64,${formData[person.key]}`}  /* Base64 */ alt="Signature" style={{ marginTop: 10, border: '1px solid black', width: '100%', height: '150px' }} />}
                    </Grid>
                  ))}
                </Grid>

                <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
                  <DialogTitle>Provide Your Signature</DialogTitle>
                  <DialogContent>
                    <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
                    <Button onClick={handleSignatureSave}>Done</Button>
                    <Button onClick={handleSignatureClose} color="secondary">Close</Button>
                  </DialogActions>
                </Dialog>
              </AccordionDetails>
            </Accordion>


            <Accordion sx={{ marginTop: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Before Skin Incision (Time Out)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel control={<Checkbox checked={formData.team_introduction} onChange={handleChange} name="team_introduction" />} label="Confirm all team members have introduced themselves by name & role" />
                <hr></hr>

                <h5>Surgeon, Anesthesia professional and nurse verbally confirm</h5>
                <FormControlLabel control={<Checkbox checked={formData.patient_confirmation} onChange={handleChange} name="patient_confirmation" />} label="Patient" />
                <FormControlLabel control={<Checkbox checked={formData.site_confirmation} onChange={handleChange} name="site_confirmation" />} label="Site" />
                <FormControlLabel control={<Checkbox checked={formData.procedure_confirmation} onChange={handleChange} name="procedure_confirmation" />} label="Procedure" />
                <hr></hr>
                <h5>Anticipated critical event</h5>
                <FormControlLabel control={<Checkbox checked={formData.anticipated_critical_event} onChange={handleChange} name="anticipated_critical_event" />} label="Surgeon View:What are the critical or unexpected steps, operative donation anticipated blood loss?" />
                <FormControlLabel control={<Checkbox checked={formData.anesthesia_team_review} onChange={handleChange} name="anesthesia_team_review" />} label="Anesthesia Team ReviewsWhat are the critical or unexpected steps, operative donation anticipated blood loss?" />
                <FormControlLabel control={<Checkbox checked={formData.nursing_team_review} onChange={handleChange} name="nursing_team_review" />} label="Nursing Team Reviews: Has seterility (icluding indicator results) be confirmed? Are There equipment issues or any concerns " />

                <hr></hr>
                <h5>Has antibiotic prophylaxis been given within the last 60 minutes</h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.antibiotic_prophylaxis === "yes"} // Only check the "Yes" checkbox if the value is "yes"
                      onChange={(event) => handleCheckboxChange(event, "yes", "antibiotic_prophylaxis")}
                      name="antibiotic_prophylaxis_yes"
                    />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.antibiotic_prophylaxis === "no"} // Only check the "NA" checkbox if the value is "na"
                      onChange={(event) => handleCheckboxChange(event, "no", "antibiotic_prophylaxis")}
                      name="antibiotic_prophylaxis_no"
                    />
                  }
                  label="NA"
                />
                <h5>Is essential imaging displayed?</h5>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.essential_imaging_displayed === "yes"} // Only check the "Yes" checkbox if the value is "yes"
                      onChange={(event) => handleCheckboxChange(event, "yes", "essential_imaging_displayed")}
                      name="essential_imaging_displayed_yes"
                    />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.essential_imaging_displayed === "no"} // Only check the "NA" checkbox if the value is "na"
                      onChange={(event) => handleCheckboxChange(event, "no", "essential_imaging_displayed")}
                      name="essential_imaging_displayed_no"
                    />
                  }
                  label="NA"
                />
                <hr></hr>
                <center><h5>ANESTHESIOLOGIST</h5></center>
                <Grid container spacing={2}>
                  {[
                    {

                      key: "anesthesiologist_signature",
                      key1: "anesthesiologist_name",

                    },


                  ].map((person, index) => (
                    <Grid item xs={12} key={index}>
                      <FormControl fullWidth sx={{ marginTop: 1 }}>
                        <InputLabel id={`${person.key1}-label`}>Name ({person.label})</InputLabel>
                        <Select
                          labelId={`${person.key1}-label`}
                          name={person.key1}
                          value={formData[person.key1]}
                          onChange={handleNameChange}
                          onBlur={handleBlur}
                          error={!!errors[person.key1]}
                          label={`Name (${person.label})`}
                        >
                          <MenuItem value="">
                            <em>Select Doctor</em>
                          </MenuItem>
                          {(doctorGroups["AnaesthesiaSpecialists"] || []).map((doctor, idx) => (
                            <MenuItem key={idx} value={doctor}>
                              {doctor}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors[person.key1] && <FormHelperText>{errors[person.key1]}</FormHelperText>}
                      </FormControl>


                      <Button variant="outlined" color="primary" sx={{ marginTop: 2 }} onClick={() => handleSignatureOpen(person.key)}> Sign Here </Button>
                      {formData[person.key] && <img src={`data:image/png;base64,${formData[person.key]}`}  /* Base64 */ alt="Signature" style={{ marginTop: 10, border: '1px solid black', width: '100%', height: '150px' }} />}
                    </Grid>
                  ))}
                </Grid>

                <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
                  <DialogTitle>Provide Your Signature</DialogTitle>
                  <DialogContent>
                    <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
                    <Button onClick={handleSignatureSave}>Done</Button>
                    <Button onClick={handleSignatureClose} color="secondary">Close</Button>
                  </DialogActions>
                </Dialog>
              </AccordionDetails>
            </Accordion>


            <Accordion sx={{ marginTop: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Before Patient Leaves OR (Sign Out)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel control={<Checkbox checked={formData.nurse_verbal_confirmation} onChange={handleChange} name="nurse_verbal_confirmation" />} label="Nurse verbally confirms with the team" />
                <hr></hr>
                <FormControlLabel control={<Checkbox checked={formData.procedure_recorded} onChange={handleChange} name="procedure_recorded" />} label="The name of the procedure recorded" />
                <hr></hr>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.instrument_sponge_needle_count_checked === "yes"} // Only check "checked & recorded" if the value is "yes"
                      onChange={(event) => handleCheckboxChange(event, "yes", "instrument_sponge_needle_count_checked")}
                      name="instrument_sponge_needle_count_checked_yes"
                    />
                  }
                  label="Checked & Recorded"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.instrument_sponge_needle_count_checked === "no"} // Only check "NA" if the value is "na"
                      onChange={(event) => handleCheckboxChange(event, "no", "instrument_sponge_needle_count_checked")}
                      name="instrument_sponge_needle_count_checked_no"
                    />
                  }
                  label="NA"
                />
                <FormControlLabel control={<Checkbox checked={formData.specimen_labelled} onChange={handleChange} name="specimen_labelled" />} label="Has the Specimen been labeled" />
                <FormControlLabel control={<Checkbox checked={formData.equipment_problem} onChange={handleChange} name="equipment_problem" />} label="Whether there are any equipment problem to be addressed" />
                <hr></hr>
                <FormControlLabel control={<Checkbox checked={formData.recovery_concerns} onChange={handleChange} name="recovery_concerns" />} label="Surgeon,anesthesia professional & nurse review the key concerns for recovery and management of patient" />
                {/*  <FormControlLabel control={<Checkbox checked={formData.recovery_concerns} onChange={handleChange} name="recovery_concerns" />} label="Post-Operative instruction" /> */}
                <FormControlLabel control={<Checkbox checked={formData.post_op_instruction_by_surgeon} onChange={handleChange} name="post_op_instruction_by_surgeon" />} label="By surgeon" />
                <FormControlLabel control={<Checkbox checked={formData.post_op_instruction_by_anesthesiologist} onChange={handleChange} name="post_op_instruction_by_anesthesiologist" />} label="By Anesthesiologist" />
                <FormControlLabel control={<Checkbox checked={formData.recovery_charting_done} onChange={handleChange} name="recovery_charting_done" />} label="Recovery charling done" />

                <hr></hr>
                <center><h5>SURGEON</h5></center>
                <Grid container spacing={2}>
                  {[
                    {
                      key: "surgeon_signature",
                      key1: "surgeon_name",
                      label: "Surgeon"
                    }
                  ].map((person, index) => (
                    <Grid item xs={12} key={index}>
                      <FormControl fullWidth sx={{ marginTop: 1 }}>
                        <InputLabel>{`Name (${person.label})`}</InputLabel>
                        <Select
                          name={person.key1}
                          value={formData[person.key1]}
                          onChange={handleNameChange}
                          onBlur={handleBlur}
                          error={!!errors[person.key1]}
                          label={`Name (${person.label})`}
                        >
                          <MenuItem value="">
                            <em>Select Doctor</em>
                          </MenuItem>
                          {Object.entries(doctorGroups).map(([specialty, doctors]) => [
                            <ListSubheader key={specialty}>{specialty}</ListSubheader>,
                            doctors.map((doctor, idx) => (
                              <MenuItem key={`${specialty}-${idx}`} value={doctor}>
                                {doctor}
                              </MenuItem>
                            ))
                          ])}
                        </Select>
                        {errors[person.key1] && <FormHelperText>{errors[person.key1]}</FormHelperText>}
                      </FormControl>

                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{ marginTop: 2 }}
                        onClick={() => handleSignatureOpen(person.key)}
                      >
                        Sign Here
                      </Button>

                      {formData[person.key] && (
                        <img
                          src={`data:image/png;base64,${formData[person.key]}`}
                          alt="Signature"
                          style={{
                            marginTop: 10,
                            border: "1px solid black",
                            width: "100%",
                            height: "150px"
                          }}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>


                <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
                  <DialogTitle>Provide Your Signature</DialogTitle>
                  <DialogContent>
                    <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
                    <Button onClick={handleSignatureSave}>Done</Button>
                    <Button onClick={handleSignatureClose} color="secondary">Close</Button>
                  </DialogActions>
                </Dialog>

              </AccordionDetails>
            </Accordion>
            <Grid >
              <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
                <DraftSaveButton
                  formKey={DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST}
                  uhidNo={uhid_no}
                  formData={formData}
                  onDraftLoaded={(draftData) => setFormData(draftData)}
                  variant="outlined"
                  size="large"
                  isUpdate={success === "success"}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                >
                  {success === "success" ? 'Update ' : 'Submit'}
                </Button>
              </Box>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default SurgicalSafetyChecklist;
